import { useContext, useEffect, useRef, type RefObject } from 'react';
import { UNSAFE_DataRouterContext, useBlocker } from 'react-router';

// A navigation the guard is holding until the user decides.
export interface HeldNavigation {
  proceed: () => void;
  reset: () => void;
}

interface UnsavedChangesGuardProps {
  // Set by the owner just before a navigation it starts itself, such as
  // leaving edit mode after the user chose to discard, so the guard lets that
  // one through.
  allowRef?: RefObject<boolean>;
  // Called when a navigation away is held. The owner asks the user, then calls
  // proceed or reset.
  onBlocked: (navigation: HeldNavigation) => void;
  // True while there is unsaved work.
  when: boolean;
}

// Browsers show their own wording; the text only has to be non-empty for the
// older ones that still read it.
const LEAVE_WARNING = 'You have unsaved changes.';

/**
 * Keeps unsaved work from being lost by leaving the page. While `when` is
 * true, it holds in-app navigation to another path or query until the owner
 * says what to do, and asks the browser to warn before a reload or close.
 *
 * `useBlocker` needs a data router, and hooks cannot be called conditionally,
 * so the navigation hold is a child rendered only inside one. Without a data
 * router (as in unit tests with a MemoryRouter) only the unload warning is
 * set, like ContentPage's own guard.
 */
export function UnsavedChangesGuard({ allowRef, onBlocked, when }: UnsavedChangesGuardProps) {
  const dataRouter = useContext(UNSAFE_DataRouterContext);

  useEffect(() => {
    if (!when) {
      return undefined;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = LEAVE_WARNING;
      return LEAVE_WARNING;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [when]);

  return dataRouter ? (
    <NavigationHold allowRef={allowRef} onBlocked={onBlocked} when={when} />
  ) : null;
}

function NavigationHold({ allowRef, onBlocked, when }: UnsavedChangesGuardProps) {
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!when || allowRef?.current) {
      return false;
    }
    return (
      currentLocation.pathname !== nextLocation.pathname ||
      currentLocation.search !== nextLocation.search
    );
  });

  const onBlockedRef = useRef(onBlocked);
  useEffect(() => {
    onBlockedRef.current = onBlocked;
  }, [onBlocked]);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      onBlockedRef.current({
        proceed: () => blocker.proceed(),
        reset: () => blocker.reset(),
      });
    }
  }, [blocker]);

  return null;
}
